'use client';

import { createClient } from '@/lib/supabase/client';
import type { SurveyRecord } from '@/types/survey';
import type { SurveyStatus } from '@/components/ui/StatusBadge';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const cls = error.code.substring(0, 2);
    if (cls === '42' || cls === '08') return true;
    if (cls === '23') return false;
  }
  if (error.message) {
    const patterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /type.*does not exist/i,
    ];
    return patterns.some((p) => p.test(error.message));
  }
  return false;
}

function formatTimestamp(ts: string): string {
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function rowToRecord(row: any): SurveyRecord {
  return {
    id: row.id,
    pid: row.pid,
    username: row.username,
    status: row.survey_status as SurveyStatus,
    ip: row.ip,
    timestamp: formatTimestamp(row.recorded_at),
  };
}

// ─── Service ──────────────────────────────────────────────────────────────────

export const surveyService = {
  /** Fetch all survey records ordered by most recent first */
  async getAll(): Promise<SurveyRecord[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('survey_records')
        .select('*')
        .order('recorded_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Survey fetch error:', error.message);
        return [];
      }
      return (data ?? []).map(rowToRecord);
    } catch (err: any) {
      console.log('Survey service error:', err.message);
      throw err;
    }
  },

  /** Insert a new survey record */
  async create(record: Omit<SurveyRecord, 'id' | 'isNew' | 'isExiting'>): Promise<SurveyRecord | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('survey_records')
        .insert({
          pid: record.pid,
          username: record.username,
          survey_status: record.status,
          ip: record.ip,
          recorded_at: new Date(record.timestamp.replace(' ', 'T')).toISOString(),
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Survey create error:', error.message);
        return null;
      }
      return rowToRecord(data);
    } catch (err: any) {
      console.log('Survey service error:', err.message);
      throw err;
    }
  },

  /** Update an existing survey record */
  async update(record: SurveyRecord): Promise<SurveyRecord | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('survey_records')
        .update({
          pid: record.pid,
          username: record.username,
          survey_status: record.status,
          ip: record.ip,
        })
        .eq('id', record.id)
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Survey update error:', error.message);
        return null;
      }
      return rowToRecord(data);
    } catch (err: any) {
      console.log('Survey service error:', err.message);
      throw err;
    }
  },

  /** Delete a survey record by ID */
  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('survey_records')
        .delete()
        .eq('id', id);

      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Survey delete error:', error.message);
        return false;
      }
      return true;
    } catch (err: any) {
      console.log('Survey service error:', err.message);
      throw err;
    }
  },

  /** Delete multiple survey records by IDs */
  async deleteMany(ids: string[]): Promise<boolean> {
    if (ids.length === 0) return true;
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('survey_records')
        .delete()
        .in('id', ids);

      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Survey bulk delete error:', error.message);
        return false;
      }
      return true;
    } catch (err: any) {
      console.log('Survey service error:', err.message);
      throw err;
    }
  },

  /** Check if a record with the same pid+username already exists */
  async exists(pid: string, username: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { count, error } = await supabase
        .from('survey_records')
        .select('*', { count: 'exact', head: true })
        .eq('pid', pid)
        .eq('username', username);

      if (error) return false;
      return (count ?? 0) > 0;
    } catch {
      return false;
    }
  },
};
